// Gunsmith models: the AWP (bolt-action scoped rifle), the Pumpy (pump-action ink shotgun) and the Gunker (grease-gun
// sprayer whose power climbs as its tank empties). Each registers its own procedural model + part animation
// (character-weapons.js registerWeaponModel) and its own menu icon (WEAPON_ICONS). Weapon space: grip centre at the
// origin, +Z = barrel forward, +Y = up, the character's right = -X (see character-weapons.js).
//
//   awp     parts: trigger · bolt (handle + body: lifts, draws back, pushes home and drops after every shot) · lens (scope
//                  glow, brightens as you scope in) · ports (muzzle-brake heat) · led (chamber lamp: green ready, red cycling)
//   pumpy   parts: trigger · pump (forend slide, racked by the pump press) · shell (the round in the ejection port: shown
//                  while chambered, thrown out on the rack) · led
//   dregs   parts: trigger · rod (plunger: pushes forward as the tank drains) · tank (team ink column, shrinks with the
//                  level) · core (power lamp: dim when full, white-hot when nearly empty)
import * as THREE from 'three';
import { registerWeaponModel, GEO_KIT, makeLampMaterial } from '../character-weapons.js';
import { superEllipsoid, lathe, smoothProfile, sweep } from '../character-geo.js';
import { WEAPON_ICONS } from '../../ui/ui-icons.js';
import { PLAYER } from '../../config.js';

const { Parts, C, M, latheZ, torus, at, screw, orient } = GEO_KIT;
const V3 = THREE.Vector3;

function part(P, pivot, mat = 'body', lamp = null) { return { src: P.build(), pivot: pivot.clone(), mat, lamp }; }

// ---------------------------------------------------------------------------------------------- shared pistol grip
const GA = new V3(0, 1, 0.25).normalize();
const GRIP_R = { pos: new V3(0, 0, 0), handZ: GA.clone(), handY: new V3(0, 0.25, -1) };
const TRIG_PIVOT = new V3(0, 0.0315, 0.0282);
function grip(P, T, col = C.darker) {
  const tilt = Math.atan2(GA.z, GA.y);
  const core = superEllipsoid(0.0118, 0.056, 0.0152, 0.62, 0.7, 12, 12);
  core.rotateX(tilt); P.add(at(core, 0, -0.008, -0.002), col, M.satin);
  for (const sx of [1, -1]) {
    const pn = superEllipsoid(0.003, 0.038, 0.0118, 0.5, 0.55, 5, 8);
    pn.rotateX(tilt); P.add(at(pn, sx * 0.0104, -0.012, -0.004), C.rubber, M.rubber);
  }
  const base = superEllipsoid(0.0138, 0.0048, 0.0188, 0.45, 0.5, 10, 4);
  base.rotateX(tilt); P.add(at(base, 0, -0.062, -0.018), C.dark, M.gloss);
  const guard = sweep([new V3(0, 0.03, 0.012), new V3(0, 0.018, 0.043), new V3(0, 0.002, 0.05), new V3(0, -0.006, 0.028)],
    { seg: 8, radial: 5, capSteps: 2, radius: () => 0.0032, flat: 1.9, outward: (Pp, o) => o.set(1, 0, 0) });
  P.add(guard.geo, C.dark, M.satin);
  const trig = superEllipsoid(0.0034, 0.0105, 0.0034, 0.7, 0.7, 6, 6, (q) => { q.z += 16 * q.y * q.y; });
  trig.rotateX(-0.25); T.add(at(trig, 0, 0.0215, 0.0305), C.metal, M.metal);
}

// ================================================================================================ AWP
function buildAwp() {
  const P = new Parts(), I = new Parts(), T = new Parts(), BOLT = new Parts(), LENS = new Parts(), PORT = new Parts(), LED = new Parts();
  grip(P, T);
  // receiver: long dark shell over a gunmetal lower, team-ink spine
  P.add(at(superEllipsoid(0.021, 0.03, 0.165, 0.4, 0.5, 14, 10), 0, 0.068, 0.03), C.dark, M.satin);
  P.add(at(superEllipsoid(0.019, 0.012, 0.15, 0.4, 0.5, 12, 6), 0, 0.041, 0.035), C.gunmetal, M.metal);
  I.add(at(superEllipsoid(0.0115, 0.005, 0.12, 0.5, 0.6, 12, 5), 0, 0.0985, 0.03));
  // skeleton stock: butt block, cheek riser, rubber recoil pad
  P.add(at(superEllipsoid(0.014, 0.036, 0.075, 0.4, 0.5, 10, 8, (q) => { q.y *= 1 + 0.35 * (-q.z / 0.075) * (q.z < 0 ? 1 : 0); }), 0, 0.05, -0.19), C.dark, M.satin);
  P.add(at(superEllipsoid(0.013, 0.016, 0.05, 0.4, 0.5, 8, 6), 0, 0.094, -0.15), C.bone, M.satin);
  P.add(at(superEllipsoid(0.017, 0.046, 0.0075, 0.4, 0.4, 8, 6), 0, 0.045, -0.262), C.rubber, M.rubber);
  I.add(at(superEllipsoid(0.0145, 0.004, 0.05, 0.5, 0.6, 8, 4), 0, 0.012, -0.19));
  // heavy fluted barrel + brake, rings, team collar
  const barrel = latheZ(smoothProfile([[0, 0.12], [0.0145, 0.12], [0.0145, 0.16], [0.0118, 0.19], [0.0118, 0.56], [0.0128, 0.58], [0.0128, 0.6]], 10), 14);
  P.add(at(barrel, 0, 0.066, 0), C.metal, M.metal);
  for (let k = 0; k < 7; k++) P.add(at(torus(0.0123, 0.0012, 3, 12), 0, 0.066, 0.22 + k * 0.05), C.gunmetal, M.metal);
  const brake = latheZ([[0, 0.598], [0.0128, 0.598], [0.0185, 0.605], [0.0195, 0.612], [0.0195, 0.668], [0.0165, 0.675], [0.0098, 0.68], [0.0098, 0.674], [0, 0.672]], 12);
  P.add(at(brake, 0, 0.066, 0), C.darker, M.satin);
  I.add(at(torus(0.0132, 0.0034, 4, 14), 0, 0.066, 0.585));
  for (let k = 0; k < 3; k++) PORT.add(at(superEllipsoid(0.0205, 0.0028, 0.0058, 0.5, 0.5, 8, 4), 0, 0.066, 0.62 + k * 0.016), C.red, M.led);
  // big scope on two rings: bell front, eyepiece back, glowing lens, turrets
  P.add(at(latheZ([[0, -0.1], [0.011, -0.1], [0.0125, -0.085], [0.0116, -0.06], [0.0116, 0.07], [0.0148, 0.092], [0.0176, 0.115], [0.0176, 0.15], [0, 0.15]], 14), 0, 0.132, 0.03), C.darker, M.satin);
  P.add(at(latheZ([[0, -0.0995], [0.0102, -0.0995], [0.0102, -0.1]], 10), 0, 0.132, 0.03), C.lens, M.lens);
  LENS.add(at(latheZ([[0, 0.1505], [0.0158, 0.1505], [0.0158, 0.1498]], 12), 0, 0.132, 0.03), C.lens, M.lens);
  for (const z of [-0.035, 0.085]) P.add(at(superEllipsoid(0.012, 0.022, 0.011, 0.4, 0.5, 8, 6), 0, 0.103, z), C.dark, M.satin);
  P.add(at(superEllipsoid(0.007, 0.0065, 0.009, 0.6, 0.6, 6, 5), 0, 0.15, 0.03), C.metal, M.metal);
  P.add(at(superEllipsoid(0.0065, 0.007, 0.009, 0.6, 0.6, 6, 5), 0.0225, 0.134, 0.03), C.metal, M.metal);
  // magazine / ink tank under the action, with a team-ink window
  P.add(at(superEllipsoid(0.0215, 0.03, 0.045, 0.4, 0.45, 10, 8), 0, 0.016, 0.03), C.dark, M.satin);
  I.add(at(superEllipsoid(0.0224, 0.02, 0.03, 0.5, 0.5, 10, 6), 0, 0.02, 0.03));
  // folded bipod under the nose + forend block for the left hand
  for (const sx of [1, -1]) P.add(at(superEllipsoid(0.004, 0.004, 0.065, 0.6, 0.6, 5, 4), sx * 0.016, 0.034, 0.34), C.gunmetal, M.metal);
  P.add(at(superEllipsoid(0.02, 0.016, 0.05, 0.4, 0.5, 8, 6), 0, 0.044, 0.205), C.rubber, M.rubber);
  // bolt: body on the right of the action + a swept handle with a ball knob (all one moving part)
  BOLT.add(at(latheZ([[0, -0.12], [0.0075, -0.12], [0.0085, -0.1], [0.0085, -0.02], [0, -0.02]], 10), -0.011, 0.077, 0), C.metal, M.metal);
  const stem = sweep([new V3(-0.011, 0.077, -0.055), new V3(-0.03, 0.074, -0.056), new V3(-0.048, 0.062, -0.057)],
    { seg: 6, radial: 6, capSteps: 2, radius: () => 0.0042, flat: 1, outward: (Pp, o) => o.set(0, 0, 1) });
  BOLT.add(stem.geo, C.metal, M.metal);
  BOLT.add(at(superEllipsoid(0.0105, 0.0105, 0.0105, 1, 1, 10, 8), -0.053, 0.059, -0.057), C.darker, M.gloss);
  LED.add(orient(superEllipsoid(0.0032, 0.0032, 0.0016, 1, 1, 8, 4), new V3(1, 0, 0), new V3(0.0212, 0.08, -0.07)), C.green, M.led);
  return {
    kind: 'awp', body: P.build(), ink: I.build(),
    parts: {
      trigger: part(T, TRIG_PIVOT),
      bolt: part(BOLT, new V3(-0.011, 0.077, -0.055)),
      lens: part(LENS, new V3(0, 0.132, 0.18), 'lamp', { color: '#0b1410', emissive: '#7dffb0', intensity: 0.2 }),
      ports: part(PORT, new V3(0, 0.066, 0.64), 'lamp', { color: '#2a0b08', emissive: '#ff5a2a', intensity: 0 }),
      led: part(LED, new V3(0.0212, 0.08, -0.07), 'lamp', { color: '#0f2a18', emissive: '#3dff7a', intensity: 1.3 }),
    },
    muzzle: new V3(0, 0.066, 0.682),
    gripR: GRIP_R,
    gripL: { pos: new V3(0, 0.044, 0.205), handZ: new V3(0, 0, 1), handY: new V3(0.75, -0.62, -0.1) },
    twirl: new V3(0, 0.03, 0.03),
  };
}

// ================================================================================================ Pumpy
function buildPumpy() {
  const P = new Parts(), I = new Parts(), T = new Parts(), PUMP = new Parts(), SHELL = new Parts(), LED = new Parts();
  grip(P, T, C.dark);
  // receiver: chunky bone-and-dark box, ink spine, ejection port window on the right flank (-X)
  P.add(at(superEllipsoid(0.027, 0.034, 0.088, 0.36, 0.42, 12, 10), 0, 0.068, 0.02), C.dark, M.satin);
  P.add(at(superEllipsoid(0.0285, 0.021, 0.062, 0.4, 0.45, 10, 6), 0, 0.066, 0.02), C.bone, M.satin);
  I.add(at(superEllipsoid(0.016, 0.0055, 0.08, 0.5, 0.6, 10, 5), 0, 0.1, 0.02));
  P.add(at(superEllipsoid(0.002, 0.012, 0.024, 0.5, 0.5, 5, 6), -0.0285, 0.074, -0.004), C.lens, M.satin);
  for (const sx of [1, -1]) for (const [y, z] of [[0.052, -0.036], [0.082, 0.05]]) screw(P, new V3(sx * 0.0288, y, z), new V3(sx, 0, 0), 0.0028);
  // stubby stock with a rubber pad
  P.add(at(superEllipsoid(0.014, 0.034, 0.06, 0.4, 0.45, 10, 8), 0, 0.054, -0.105), C.dark, M.satin);
  P.add(at(superEllipsoid(0.016, 0.04, 0.007, 0.4, 0.4, 8, 6), 0, 0.05, -0.17), C.rubber, M.rubber);
  // wide-bore barrel with a flared choke, vent rib, bead sight
  P.add(at(latheZ(smoothProfile([[0, 0.09], [0.0195, 0.09], [0.0195, 0.3], [0.0225, 0.318], [0.0245, 0.335], [0.0245, 0.352], [0.0195, 0.358], [0.0165, 0.355], [0, 0.35]], 10), 14), 0, 0.074, 0), C.gunmetal, M.metal);
  I.add(at(torus(0.0235, 0.0034, 4, 16), 0, 0.074, 0.336));
  P.add(at(superEllipsoid(0.0042, 0.0034, 0.12, 0.6, 0.6, 5, 4), 0, 0.099, 0.22), C.darker, M.satin);
  P.add(at(superEllipsoid(0.0025, 0.007, 0.004, 0.6, 0.6, 5, 4), 0, 0.103, 0.34), C.amber, M.led);
  // magazine tube under the barrel (team-ink caps), the slide rides it
  P.add(at(latheZ([[0, 0.05], [0.0125, 0.05], [0.0125, 0.32], [0.0145, 0.325], [0.0145, 0.335], [0, 0.335]], 10), 0, 0.04, 0), C.metal, M.metal);
  I.add(at(torus(0.0142, 0.0028, 4, 14), 0, 0.04, 0.3));
  P.add(at(superEllipsoid(0.014, 0.014, 0.022, 0.5, 0.5, 8, 6), 0, 0.052, 0.23), C.dark, M.satin);
  // the pump slide: ribbed rubber sleeve
  PUMP.add(at(superEllipsoid(0.0225, 0.021, 0.056, 0.4, 0.5, 10, 8), 0, 0.04, 0.17), C.rubber, M.rubber);
  for (let k = 0; k < 6; k++) PUMP.add(at(torus(0.0228, 0.0021, 3, 14), 0, 0.04, 0.132 + k * 0.014), C.darker, M.satin);
  PUMP.add(at(superEllipsoid(0.0235, 0.0235, 0.004, 0.5, 0.5, 10, 6), 0, 0.04, 0.228), C.metal, M.metal);
  // the shell sitting in the ejection port (a team-ink cartridge, brass base)
  SHELL.add(at(latheZ([[0, -0.022], [0.0072, -0.022], [0.0075, 0.014], [0.0068, 0.02], [0, 0.022]], 10), -0.0232, 0.074, -0.004).rotateY(0), C.white, M.gloss);
  SHELL.add(at(latheZ([[0, -0.026], [0.0078, -0.026], [0.0078, -0.018], [0, -0.018]], 10), -0.0232, 0.074, -0.004), C.amber, M.metal);
  LED.add(orient(superEllipsoid(0.0032, 0.0032, 0.0016, 1, 1, 8, 4), new V3(1, 0, 0), new V3(0.0288, 0.086, -0.04)), C.green, M.led);
  return {
    kind: 'pumpy', body: P.build(), ink: I.build(),
    parts: {
      trigger: part(T, TRIG_PIVOT),
      pump: part(PUMP, new V3(0, 0.04, 0.17)),
      shell: part(SHELL, new V3(-0.0232, 0.074, -0.004), 'ink'),
      led: part(LED, new V3(0.0288, 0.086, -0.04), 'lamp', { color: '#0f2a18', emissive: '#3dff7a', intensity: 1.3 }),
    },
    muzzle: new V3(0, 0.074, 0.362),
    gripR: GRIP_R,
    gripL: { pos: new V3(0, 0.04, 0.17), handZ: new V3(0, 0, 1), handY: new V3(0.8, -0.55, -0.1) },
    twirl: new V3(0, 0.03, 0.03),
  };
}

// ================================================================================================ Gunker
function buildDregs() {
  const P = new Parts(), I = new Parts(), T = new Parts(), ROD = new Parts(), TANK = new Parts(), CORE = new Parts();
  grip(P, T, C.dark);
  const Y = 0.078;
  // frame: rear plate, top spine and a cage of bars round the ink tube; the tube itself is a team-ink part that shrinks
  P.add(at(superEllipsoid(0.026, 0.046, 0.008, 0.4, 0.4, 10, 8), 0, Y - 0.002, -0.058), C.dark, M.gloss);
  P.add(at(superEllipsoid(0.03, 0.02, 0.09, 0.4, 0.5, 10, 6), 0, 0.048, 0.018), C.gunmetal, M.metal);
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * Math.PI * 2 + Math.PI / 6;
    P.add(at(superEllipsoid(0.0034, 0.0034, 0.125, 0.6, 0.6, 4, 4), Math.cos(a) * 0.0295, Y + Math.sin(a) * 0.0295, 0.065), C.dark, M.satin);
  }
  TANK.add(at(latheZ(smoothProfile([[0, -0.25], [0.0245, -0.245], [0.0262, -0.23], [0.0262, -0.01], [0.0245, -0.003], [0, 0]], 7), 14), 0, Y, 0.19), C.white, M.gloss);
  P.add(at(latheZ([[0, 0.178], [0.0295, 0.178], [0.0305, 0.19], [0.0305, 0.2], [0.0225, 0.206], [0, 0.206]], 14), 0, Y, 0), C.dark, M.gloss);
  // nozzle: a fat cone that squeezes down to a bead tip + team ring
  P.add(at(latheZ(smoothProfile([[0, 0.2], [0.0225, 0.2], [0.021, 0.222], [0.0125, 0.25], [0.0085, 0.28], [0.0105, 0.286], [0.0105, 0.294], [0, 0.294]], 8), 12), 0, Y, 0), C.gunmetal, M.metal);
  I.add(at(torus(0.0122, 0.0032, 4, 14), 0, Y, 0.264));
  // plunger rod with ratchet teeth and a thumb ring (travels forward as ink leaves the tube)
  ROD.add(at(latheZ([[0, -0.175], [0.0058, -0.175], [0.0058, 0.0], [0, 0.0]], 8), 0, Y, 0), C.metal, M.metal);
  for (let k = 0; k < 9; k++) ROD.add(at(torus(0.0072, 0.0012, 3, 10), 0, Y, -0.16 + k * 0.016), C.gunmetal, M.metal);
  ROD.add(at(torus(0.0185, 0.0036, 5, 18).rotateX(Math.PI / 2), 0, Y, -0.19), C.darker, M.rubber);
  // power core: a bar of light along the top (brightens with the power)
  CORE.add(at(superEllipsoid(0.0095, 0.0042, 0.1, 0.5, 0.6, 8, 4), 0, Y + 0.036, 0.07), C.white, M.led);
  // squeeze lever's ratchet pawl + foregrip + sight
  P.add(at(superEllipsoid(0.005, 0.01, 0.022, 0.5, 0.5, 6, 5), 0, 0.04, -0.012), C.metal, M.metal);
  const fg = superEllipsoid(0.0118, 0.026, 0.0132, 0.55, 0.65, 10, 8); fg.rotateX(-0.12); P.add(at(fg, 0, 0.018, 0.1), C.darker, M.satin);
  P.add(at(superEllipsoid(0.0125, 0.0175, 0.0095, 0.5, 0.55, 8, 6).rotateX(-0.12), 0, 0.012, 0.099), C.rubber, M.rubber);
  P.add(at(superEllipsoid(0.002, 0.008, 0.004, 0.6, 0.6, 5, 4), 0, Y + 0.045, 0.19), C.amber, M.led);
  return {
    kind: 'dregs', body: P.build(), ink: I.build(),
    parts: {
      trigger: part(T, TRIG_PIVOT),
      rod: part(ROD, new V3(0, Y, 0)),
      tank: part(TANK, new V3(0, Y, 0.19), 'ink'),
      core: part(CORE, new V3(0, Y + 0.036, 0.07), 'lamp', { color: '#1a1a1a', emissive: '#ffffff', intensity: 0.2 }),
    },
    muzzle: new V3(0, Y, 0.296),
    gripR: GRIP_R,
    gripL: { pos: new V3(0, 0.02, 0.1), handZ: new V3(0, 1, -0.12), handY: new V3(0.45, -0.05, -1) },
    twirl: new V3(0, 0.03, 0.03),
  };
}

// ---------------------------------------------------------------------------------------------- animation
const mj = (t) => { t = Math.min(1, Math.max(0, t)); return t * t * t * (10 + t * (6 * t - 15)); };
const damp = (a, b, l, dt) => a + (b - a) * (1 - Math.exp(-l * dt));
const _w = new THREE.Color(1, 1, 1);
function lod(w, st) {
  const near = st.near !== false;
  if (near !== w.near) {
    w.near = near;
    if (w.body) w.body.visible = near; if (w.ink) w.ink.visible = near;
    if (w.bodyFar) w.bodyFar.visible = !near; if (w.inkFar) w.inkFar.visible = !near;
    for (const g of w.partList || []) g.visible = near;
  }
  return near;
}
function lamp(g, col, k) { const m = g?.userData.mesh.material; if (m) { m.emissive.copy(col); m.emissiveIntensity = k; } }
function rest(g) { return g.userData.rest; }

// AWP bolt cycle after the shot (seconds since the shot): settle → lift the handle → draw back → hold → push → drop
export const AWP_BOLT = { lift: [0.3, 0.52], draw: [0.52, 1.0], hold: [1.0, 1.2], push: [1.2, 1.5], drop: [1.5, 1.78] };
function boltPhase(u) {
  const B = AWP_BOLT;
  const lift = u < B.lift[0] ? 0 : u < B.lift[1] ? mj((u - B.lift[0]) / (B.lift[1] - B.lift[0])) : u < B.drop[0] ? 1 : 1 - mj((u - B.drop[0]) / (B.drop[1] - B.drop[0]));
  const draw = u < B.draw[0] ? 0 : u < B.draw[1] ? mj((u - B.draw[0]) / (B.draw[1] - B.draw[0])) : u < B.push[0] ? 1 : 1 - mj((u - B.push[0]) / (B.push[1] - B.push[0]));
  return { lift, draw };
}
function animateAwp(w, st) {
  const P = w.parts || {}, R = st.runner, t = st.t, dt = Math.min(0.1, Math.max(0, st.dt || 0)), col = st.color || _w;
  if (!w.ps) { w.ps = new Float32Array(12); w.pump = 0; w.trig = 0; w.heat = 0; w.near = true; }
  if (!lod(w, st)) return;
  const rel = st.sinceRelease ?? 99, sc = st.charge || 0;
  const bp = boltPhase(rel);
  if (P.bolt) { P.bolt.rotation.z = -1.15 * bp.lift; P.bolt.position.z = rest(P.bolt).z - 0.075 * bp.draw; }
  if (P.trigger) { w.trig = damp(w.trig, sc > 0.01 ? 1 : 0, 30, dt); P.trigger.rotation.x = 0.42 * w.trig; }
  // scope lens glows as the breath is held; the muzzle brake runs hot after the shot
  lamp(P.lens, col.clone().lerp(_w, 0.35), 0.12 + 3.4 * sc * sc + (sc >= 0.999 ? 0.9 + 0.5 * Math.sin(t * 28) : 0));
  if (rel < dt * 1.5) w.heat = 1;
  w.heat *= Math.exp(-dt * 1.7);
  lamp(P.ports, _w.clone().multiplyScalar(0.6).add(col.clone().multiplyScalar(0.4)), 6 * w.heat * w.heat);
  // chamber lamp: red while the bolt cycles (the tell enemies read), green when ready
  const cycling = rel < AWP_BOLT.drop[1];
  const m = P.led?.userData.mesh.material;
  if (m) { if (cycling) { m.emissive.setRGB(1, 0.14, 0.1); m.emissiveIntensity = 1.5 + 2.2 * (0.5 + 0.5 * Math.sin(t * 22)); } else { m.emissive.setRGB(0.24, 1, 0.48); m.emissiveIntensity = 1.2; } }
}

// Pumpy: the rack. pumpAge = seconds since the pump press (the runner's clock; remote copies fall back to the shot clock)
function animatePumpy(w, st) {
  const P = w.parts || {}, R = st.runner, k = R?.kit, dt = Math.min(0.1, Math.max(0, st.dt || 0)), t = st.t;
  if (!w.ps) { w.ps = new Float32Array(12); w.pump = 0; w.trig = 0; w.near = true; w.shellK = 1; w.shellA = 0; }
  const ts = st.sinceShoot ?? 99;
  const age = k ? k.pumpAge : ts - 0.32, chambered = k ? k.chambered : ts > 0.9 || ts < 0.05;
  const pt = k && k.pumpDur ? k.pumpDur : 0.5;
  let pk = 0;                                   // 0 home → 1 racked all the way back → 0
  if (age >= 0 && age < pt) { const u = age / pt; pk = u < 0.45 ? mj(u / 0.45) : u < 0.55 ? 1 : 1 - mj((u - 0.55) / 0.45); }
  w.pump = pk;                                   // the left hand rides this (character.js)
  if (!lod(w, st)) return;
  if (P.pump) P.pump.position.z = rest(P.pump).z - 0.04 * pk;
  if (P.trigger) { w.trig = damp(w.trig, ts < 0.07 ? 1 : 0, ts < 0.07 ? 45 : 22, dt); P.trigger.rotation.x = 0.42 * w.trig; }
  // the shell: gone when the gun is empty, thrown out through the port on the back stroke, a fresh one rides in on the return
  const sh = P.shell;
  if (sh) {
    const want = chambered ? 1 : 0;
    w.shellK = damp(w.shellK, want, want > w.shellK ? 14 : 40, dt);
    const out = age >= 0 && age < pt ? mj((age / pt - 0.18) / 0.3) * (1 - mj((age / pt - 0.5) / 0.1)) : 0;
    sh.position.set(rest(sh).x - 0.05 * out, rest(sh).y + 0.045 * out, rest(sh).z - 0.01 * out);
    sh.rotation.z = 2.4 * out; sh.scale.setScalar(Math.max(0.001, w.shellK));
  }
  const m = P.led?.userData.mesh.material;
  if (m) {
    if (!chambered) { m.emissive.setRGB(1, 0.16, 0.1); m.emissiveIntensity = 1.4 + 1.6 * (0.5 + 0.5 * Math.sin(t * 16)); }
    else { m.emissive.setRGB(0.24, 1, 0.48); m.emissiveIntensity = 1.2; }
  }
}

// Gunker: the ink column and plunger are the fuel gauge; the core is the power gauge
function animateDregs(w, st) {
  const P = w.parts || {}, R = st.runner, dt = Math.min(0.1, Math.max(0, st.dt || 0)), t = st.t, col = st.color || _w;
  if (!w.ps) { w.ps = new Float32Array(12); w.pump = 0; w.trig = 0; w.near = true; w.fill = 1; }
  if (!lod(w, st)) return;
  const frac = R ? Math.min(1, Math.max(0, R.a.ink / PLAYER.inkMax)) : 1 - (st.lowInk || 0);
  w.fill = damp(w.fill, frac, 12, dt);
  const power = 1 - w.fill;
  if (P.tank) { P.tank.scale.z = Math.max(0.02, w.fill); }
  if (P.rod) P.rod.position.z = rest(P.rod).z + 0.125 * power;
  if (P.trigger) { w.trig = damp(w.trig, st.firing && (st.sinceShoot ?? 99) < 0.16 ? 1 : 0, 40, dt); P.trigger.rotation.x = 0.5 * w.trig; }
  lamp(P.core, col.clone().lerp(_w, power * 0.85), 0.15 + 5.5 * power * power + 0.6 * power * Math.sin(t * 30));
}

registerWeaponModel('awp', buildAwp, animateAwp);
registerWeaponModel('pumpy', buildPumpy, animatePumpy);
registerWeaponModel('dregs', buildDregs, animateDregs);

// ---------------------------------------------------------------------------------------------- icons
// (same kit as ui-icons.js: 64² box, ink outline, dark / light plastic, team ink = currentColor, white glints)
const K = '#15121c', DK = '#2b2735', LT = '#e4e8ef';
const O = `stroke="${K}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"`;
WEAPON_ICONS.awp = `<svg class="iw-ico " viewBox="0 0 64 64" aria-hidden="true">
  <g ${O}>
    <path d="M3 29 L14 27 L16 40 L3 41 Z" fill="${DK}"/>
    <path d="M15 28 L36 28 L36 38 L15 38 Z" fill="${LT}"/>
    <path d="M36 31 L61 30 L61 35 L36 35 Z" fill="${DK}"/>
    <path d="M59 28.5 L63 28.5 L63 36.5 L59 36.5 Z" fill="${K}"/>
    <path d="M18 21 L40 21 L40 26 L18 26 Z" fill="${DK}"/>
    <path d="M38 19.5 L46 19 L46 27 L38 26.5 Z" fill="${LT}"/>
    <path d="M18 26 L20 28 M36 26 L34 28" fill="none"/>
    <path d="M22 38 L34 38 L33 46 L23 46 Z" fill="currentColor"/>
    <path d="M25 38 L25 46" fill="none"/>
    <path d="M16 36 Q12 44 15 51 L21 51 L22 38" fill="${DK}"/>
    <path d="M26 28 L26 22 L22 21" fill="none"/>
    <path d="M30 28 L33 24" fill="none" stroke-width="2.4"/>
  </g>
  <circle cx="33" cy="24.5" r="2.4" fill="${LT}" stroke="${K}" stroke-width="1.6"/>
  <path d="M44 22 L44 24.5" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>
</svg>`;
WEAPON_ICONS.pumpy = `<svg class="iw-ico " viewBox="0 0 64 64" aria-hidden="true">
  <g ${O}>
    <path d="M3 30 L16 28 L18 41 L5 43 Z" fill="${DK}"/>
    <path d="M17 26 L38 26 L38 40 L17 40 Z" fill="${LT}"/>
    <path d="M38 27.5 L58 27.5 L58 33.5 L38 33.5 Z" fill="${DK}"/>
    <path d="M56 25.5 L62 25 L62 36 L56 35.5 Z" fill="currentColor"/>
    <path d="M38 36 L56 36 L56 40 L38 40 Z" fill="${DK}"/>
    <path d="M41 33 L53 33 L53 43 L41 43 Z" fill="${K}"/>
    <path d="M22 40 L30 40 L28 52 L22 52 Z" fill="${DK}"/>
    <path d="M44 33.5 L44 42.5 M48 33.5 L48 42.5 M52 33.5 L52 42.5" stroke="${LT}" stroke-width="1.8" fill="none"/>
    <path d="M23 26 L29 22 L33 26" fill="${DK}"/>
  </g>
  <rect x="24" y="30" width="9" height="5" rx="2" fill="currentColor" stroke="${K}" stroke-width="1.6"/>
  <path d="M57 29 L57 32" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>
</svg>`;
WEAPON_ICONS.dregs = `<svg class="iw-ico " viewBox="0 0 64 64" aria-hidden="true">
  <g ${O}>
    <path d="M9 18 L13 18 L13 36 L9 36 Z" fill="${DK}"/>
    <path d="M3 25 L10 25 L10 29 L3 29 Z" fill="${LT}"/>
    <path d="M13 17 L45 17 L45 37 L13 37 Z" fill="${LT}"/>
    <path d="M16 20 L43 20 L43 34 L16 34 Z" fill="currentColor"/>
    <path d="M45 16 L49 16 L49 38 L45 38 Z" fill="${DK}"/>
    <path d="M49 21 L58 26 L61 26 L61 29 L58 29 L49 34 Z" fill="${DK}"/>
    <path d="M13 37 L49 37 L49 41 L13 41 Z" fill="${DK}"/>
    <path d="M22 41 L30 41 L26 58 L18 58 Z" fill="${DK}"/>
    <path d="M32 41 L38 41 L36 50 L31 49 Z" fill="${DK}"/>
  </g>
  <path d="M19 23 L40 23" stroke="#fff" stroke-width="2" stroke-linecap="round" opacity=".75"/>
  <circle cx="62" cy="27.5" r="1.5" fill="currentColor"/>
</svg>`;
